import { lazy, type ComponentType } from "react"

/**
 * Wraps a `lazy(() => import(...))` factory with stale-chunk recovery.
 *
 * The problem: when a new build deploys, the HTML shell of an
 * already-open tab still references the previous chunk hashes. The
 * next lazy route navigation issues an `import()` for a URL that no
 * longer exists on the CDN — Vite / the browser rejects with a
 * "Failed to fetch dynamically imported module" or "ChunkLoadError".
 * That bubbles up through Suspense to the ErrorBoundary and the whole
 * area of the app becomes unusable until the user manually refreshes.
 *
 * The fix: catch the chunk-load rejection at the import boundary and
 * do a one-shot page reload. The reload picks up the fresh shell
 * which points at the new chunk hashes and the user lands back on
 * the same route without noticing.
 *
 * Loop guard: we set a `sessionStorage` flag before reloading so a
 * genuinely-broken import (e.g. a real 500 from the CDN) doesn't
 * trigger an infinite reload cycle. The flag is cleared when the
 * page next loads successfully OR when a different lazy import
 * succeeds — either signal proves the chunk system is working.
 *
 * Usage:
 *
 *   const DashboardPage = lazyWithRetry(() =>
 *     import("./pages/DashboardPage").then((m) => ({ default: m.DashboardPage })),
 *   )
 *
 * Drop-in for every `lazy(() => import(...))` call site.
 */
const RELOAD_FLAG_KEY = "edemics:chunk-reload-attempted"

// Chunk-load errors surface with a few different messages depending on
// the runtime. Recognizing any of them means "the module we asked for
// no longer exists at that URL — try the fresh shell".
function isChunkLoadError(err: unknown): boolean {
  if (!err) return false
  const msg = err instanceof Error ? err.message : String(err)
  return (
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /Loading chunk \d+ failed/i.test(msg) ||
    /Loading CSS chunk \d+ failed/i.test(msg) ||
    /Importing a module script failed/i.test(msg) ||
    /error loading dynamically imported module/i.test(msg) ||
    // Firefox flavor
    /NetworkError when attempting to fetch resource/i.test(msg)
  )
}

function safeGetFlag(): boolean {
  try {
    return sessionStorage.getItem(RELOAD_FLAG_KEY) === "1"
  } catch {
    // sessionStorage blocked (private tab, some ITP configs) — assume
    // no prior attempt so we still reload once.
    return false
  }
}

function safeSetFlag() {
  try {
    sessionStorage.setItem(RELOAD_FLAG_KEY, "1")
  } catch {
    /* blocked — nothing else to do, we'll just take the reload */
  }
}

function safeClearFlag() {
  try {
    sessionStorage.removeItem(RELOAD_FLAG_KEY)
  } catch {
    /* blocked — flag never got set anyway */
  }
}

/**
 * Clear the reload flag once a successful chunk load lands. Call this
 * from the ErrorBoundary's "everything is fine now" path OR on any
 * navigation that renders without a chunk error — it self-heals so a
 * later real regression can still trigger a reload attempt.
 */
export function clearStaleChunkReloadFlag() {
  safeClearFlag()
}

export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
) {
  return lazy(async () => {
    try {
      const mod = await factory()
      // A successful load proves the chunk system is healthy — clear
      // any lingering reload flag from a previous stale-chunk event.
      safeClearFlag()
      return mod
    } catch (err) {
      if (isChunkLoadError(err)) {
        if (!safeGetFlag()) {
          safeSetFlag()
          // Full reload — the browser picks up the fresh HTML shell
          // whose script tags point at the current chunk hashes. Using
          // `location.reload()` rather than `location.href = ...` so
          // the URL (and any query/hash) is preserved.
          if (typeof window !== "undefined") {
            window.location.reload()
          }
          // Never resolves — the tab is reloading. Throwing here just
          // shuts up the microtask; Suspense won't render this branch.
          throw err
        }
        // Second consecutive failure — the CDN really is broken.
        // Surface to the ErrorBoundary so the user sees a proper
        // "Something went wrong" screen instead of an infinite reload.
      }
      throw err
    }
  })
}
